import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { DEAL_TYPES, type DealType, type PortalDealDetail } from "@colab/shared";
import { DealStatusBadge } from "./deal-status-badge";
import { AttributionDirectionBadge } from "./attribution-direction-badge";
import { DealCommentThread } from "./deal-comment-thread";
import { DealDecisionTimeline } from "./deal-decision-timeline";
import { useUpdatePendingDeal } from "./use-update-pending-deal";
import { useWithdrawDeal } from "./use-withdraw-deal";

// Phase 7 sub-bucket 5 — inline editor for a submitted-and-pending deal.
//
// Eligibility (server-enforced + UI guard): source='deal' AND
// status='submitted' AND partner-submitted AND not withdrawn. The detail
// page branches on `detail.can_edit` to render this vs DealReadOnlyView.
//
// No auto-save (leads are quick entries — auto-save is MDF-only per bucket 9
// invariant). Manual Save / Withdraw / Cancel buttons. Withdraw shows an
// AlertDialog confirmation; Save fires pessimistically.

type Props = { detail: PortalDealDetail };

type FormState = {
  title: string;
  deal_type: DealType;
  prospect_company: string;
  prospect_contact_name: string;
  prospect_contact_email: string;
  estimated_value_eur: string;
  notes: string;
};

function initial(detail: PortalDealDetail): FormState {
  return {
    title: detail.customer_name,
    deal_type: detail.deal_type ?? "referral",
    prospect_company: detail.customer_name,
    prospect_contact_name: detail.prospect_contact_name ?? "",
    prospect_contact_email: detail.prospect_contact_email ?? "",
    estimated_value_eur: detail.amount != null ? String(detail.amount) : "",
    notes: detail.notes ?? "",
  };
}

export function DealEditView({ detail }: Props) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => initial(detail));
  const update = useUpdatePendingDeal(detail.id);
  const withdraw = useWithdrawDeal(detail.id);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function handleSave() {
    update.mutate(
      {
        title: form.title.trim(),
        deal_type: form.deal_type,
        prospect_company: form.prospect_company.trim(),
        prospect_contact_name: form.prospect_contact_name.trim() || null,
        prospect_contact_email: form.prospect_contact_email.trim() || null,
        estimated_value_eur: form.estimated_value_eur.trim()
          ? Number(form.estimated_value_eur)
          : null,
        notes: form.notes.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("Deal updated");
        },
      },
    );
  }

  function handleWithdraw() {
    withdraw.mutate(undefined, {
      onSuccess: () => {
        toast.success("Deal withdrawn");
        navigate("/portal/deals", { replace: true });
      },
    });
  }

  return (
    <div>
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Submitted lead
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Edit deal
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <DealStatusBadge row={detail} />
            <AttributionDirectionBadge row={detail} />
          </div>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-xl border bg-card p-5">
            <h3 className="text-sm font-medium text-foreground">Lead details</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Title">
                <Input
                  value={form.title}
                  onChange={(e) => patch("title", e.target.value)}
                  placeholder="Short name for this lead"
                />
              </Field>
              <Field label="Type">
                <Select
                  value={form.deal_type}
                  onValueChange={(v) => patch("deal_type", v as DealType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {formatDealType(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Customer company">
                <Input
                  value={form.prospect_company}
                  onChange={(e) => patch("prospect_company", e.target.value)}
                  placeholder="Customer's company name"
                />
              </Field>
              <Field label="Estimated value (EUR)">
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.estimated_value_eur}
                  onChange={(e) =>
                    patch(
                      "estimated_value_eur",
                      e.target.value.replace(/[^0-9]/g, ""),
                    )
                  }
                  placeholder="0"
                />
              </Field>
              <Field label="Contact name (optional)">
                <Input
                  value={form.prospect_contact_name}
                  onChange={(e) => patch("prospect_contact_name", e.target.value)}
                />
              </Field>
              <Field label="Contact email (optional)">
                <Input
                  type="email"
                  value={form.prospect_contact_email}
                  onChange={(e) => patch("prospect_contact_email", e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Notes">
                <Textarea
                  value={form.notes}
                  onChange={(e) => patch("notes", e.target.value)}
                  rows={4}
                  placeholder="Context for the Bookboost team."
                />
              </Field>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 border-t pt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Withdraw lead
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Withdraw this lead?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Withdrawing closes the lead and signals to Bookboost
                      that you no longer want it reviewed. This cannot be
                      undone — you&rsquo;ll need to submit a new lead if you
                      change your mind.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleWithdraw}
                      disabled={withdraw.isPending}
                    >
                      {withdraw.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Withdraw lead
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setForm(initial(detail))}
                  disabled={update.isPending}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={update.isPending}
                >
                  {update.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Save changes
                </Button>
              </div>
            </div>
          </section>

          <section className="rounded-xl border bg-card p-5">
            <DealCommentThread dealId={detail.id} canPost={true} />
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-xl border bg-card p-5">
            <DealDecisionTimeline
              dealId={detail.id}
              isWithdrawn={detail.is_withdrawn}
            />
          </section>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function formatDealType(t: string): string {
  switch (t) {
    case "referral":
      return "Referral";
    case "co_sell":
      return "Co-sell";
    case "reseller":
      return "Reseller";
    default:
      return t;
  }
}
